import React, { useContext, useState } from "react";
import {
  DELETE_ACCOUNT,
  type AccountUuidVariables,
  type DeleteAccountData,
} from "../CustomerProvisioningApi";
import { showToast } from "../../Forms/Dialogs/Toast/Toast";
import type { FetchResult } from "@apollo/client";
import {
  TextButton,
  useMutationWrapper,
  AlertIndicator,
  alertTypes,
  ModalContext,
  ModalButtonsContainer,
  showErrorDialog,
} from "@SeemplicityDev/shared";

interface DisableAccountModalProps {
  accountUuid: string;
  accountName: string;
  organizationName: string;
}

const DisableAccountModal: React.FC<DisableAccountModalProps> = ({
  accountUuid,
  accountName,
  organizationName,
}) => {
  const { closeModal } = useContext(ModalContext);
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);

  // Note: cnc_delete_account is a soft-delete that disables the account (kept for backward compatibility).
  const [disableAccount] = useMutationWrapper<DeleteAccountData>(
    DELETE_ACCOUNT,
    {
      refetchQueries: ["cnc_organizations"],
    }
  );

  const onSubmit = () => {
    setIsSubmittingForm(true);

    disableAccount({
      variables: { account_uuid: accountUuid } as AccountUuidVariables,
    })
      .then((res: FetchResult<DeleteAccountData>) => {
        if (!!res?.data?.cnc_delete_account?.ok) {
          closeModal();
          showToast({ message: res?.data?.cnc_delete_account?.msg });
        } else {
          setIsSubmittingForm(false);
          showErrorDialog({ message: res.data?.cnc_delete_account?.msg });
        }
      })
      .catch((err: Error) => {
        setIsSubmittingForm(false);
        showErrorDialog({ message: err.message });
      });
  };

  return (
    <div>
      <AlertIndicator
        type={alertTypes.WARNING}
        wrapperClassName={"connection-alert"}
        title={`Disable account`}
        content={
          <div>
            This will disable account <strong>{accountName}</strong> (of{" "}
            <strong>{organizationName}</strong>). The tenant will be blocked
            from application actions and activity, but its data will be retained
            and the account can be re-enabled later.
          </div>
        }
      />
      <ModalButtonsContainer>
        <TextButton
          label={"Disable Account"}
          isSecondary
          isDanger
          rightIcon={isSubmittingForm ? "seem-icon-spinner spinner" : ""}
          isSubmitting={isSubmittingForm}
          disabled={isSubmittingForm}
          onClick={onSubmit}
        />
      </ModalButtonsContainer>
    </div>
  );
};

export default DisableAccountModal;
